import React, { useState } from 'react';
import './MediaLibrary.css';

const MediaLibrary = ({
  media,
  mediaLoading,
  typeFilter,
  teamFilter,
  teams,
  onTypeFilterChange,
  onTeamFilterChange,
  onReload,
  onUpload,
  onUploadBundle,
  onDelete,
  statusMsg,
}) => {
  const [uploadType, setUploadType] = useState('team');
  const [uploadTeam, setUploadTeam] = useState('');
  const [uploadPlayer, setUploadPlayer] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPlayerPics, setUploadPlayerPics] = useState([]);
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [bundleTeam, setBundleTeam] = useState('');
  const [bundleTeamName, setBundleTeamName] = useState('');
  const [bundleLogo, setBundleLogo] = useState(null);
  const [bundlePlayers, setBundlePlayers] = useState(
    Array.from({ length: 6 }, () => ({ name: '', file: null }))
  );
  const [expandedMap, setExpandedMap] = useState({});
  const [actionsId, setActionsId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState('');
  const [editTeamId, setEditTeamId] = useState('');
  const [editTeamName, setEditTeamName] = useState('');
  const [editPlayers, setEditPlayers] = useState([]);
  const [editLogo, setEditLogo] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (uploadType === 'team') {
      onUpload({
        files: uploadFile ? [uploadFile] : [],
        type: uploadType,
        teamId: uploadTeam,
        playerName: '',
      });
    } else {
      onUpload({
        files: uploadPlayerPics,
        type: uploadType,
        teamId: uploadTeam,
        playerName: uploadPlayer || '',
      });
    }
    setUploadFile(null);
    setUploadPlayerPics([]);
    const fileInput = document.getElementById('media-upload-file');
    if (fileInput) fileInput.value = '';
    const multiInput = document.getElementById('media-upload-multi');
    if (multiInput) multiInput.value = '';
  };

  const handleBundleSubmit = async (e) => {
    e.preventDefault();
    if (!bundleLogo) return;
    const players = bundlePlayers
      .filter((p) => p.file)
      .map((p) => ({ name: p.name, file: p.file }));
    try {
      await onUploadBundle({
        teamId: bundleTeam,
        teamName: bundleTeamName,
        logoFile: bundleLogo,
        players,
      });
    } finally {
      setBundleTeam('');
      setBundleTeamName('');
      setBundleLogo(null);
      setBundlePlayers(Array.from({ length: 6 }, () => ({ name: '', file: null })));
      const logoInput = document.getElementById('bundle-logo-file');
      if (logoInput) logoInput.value = '';
      bundlePlayers.forEach((_, idx) => {
        const input = document.getElementById(`bundle-player-file-${idx}`);
        if (input) input.value = '';
      });
      setShowBundleModal(false);
    }
  };

  return (
    <div className="sa-card">
      <div className="sa-card-header">
        <h2>Media Library</h2>
        <div className="sa-actions">
          <select
            className="sa-select"
            value={typeFilter}
            onChange={(e) => onTypeFilterChange(e.target.value)}
          >
            <option value="">All types</option>
            <option value="team">Team logos</option>
            <option value="player">Player pictures</option>
          </select>
          <select
            className="sa-select"
            value={teamFilter}
            onChange={(e) => onTeamFilterChange(e.target.value)}
          >
            <option value="">All teams</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name || t.teamName || t.id}
              </option>
            ))}
          </select>
          <button className="sa-btn ghost" onClick={onReload} disabled={mediaLoading}>
            {mediaLoading ? 'Loading...' : 'Reload'}
          </button>
          <button
            className="sa-btn"
            onClick={() => setShowBundleModal(prev => !prev)}
            type="button"
          >
            Bulk add team
          </button>
        </div>
      </div>

      {statusMsg && <div className="sa-status">{statusMsg}</div>}

      {/* Legacy single upload hidden since bulk modal is primary */}
      {/* <form className="media-upload" onSubmit={handleSubmit}> ... </form> */}

      {showBundleModal && (
        <div className="sa-modal-backdrop" onClick={() => setShowBundleModal(false)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sa-modal-header">
              <h3>Bulk Team Upload</h3>
              <button className="sa-btn ghost" onClick={() => setShowBundleModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleBundleSubmit} className="bundle-form">
              <div className="bundle-row">
                <select
                  className="sa-select"
                  value={bundleTeam}
                  onChange={(e) => setBundleTeam(e.target.value)}
                >
                  <option value="">Select team (optional)</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name || t.teamName || t.id}
                    </option>
                  ))}
                </select>
                <input
                  className="sa-input"
                  placeholder="Team name"
                  value={bundleTeamName}
                  onChange={(e) => setBundleTeamName(e.target.value)}
                />
                <input
                  id="bundle-logo-file"
                  type="file"
                  accept="image/png"
                  className="sa-input"
                  onChange={(e) => setBundleLogo(e.target.files?.[0] || null)}
                />
              </div>
              <div className="bundle-players">
                {bundlePlayers.map((p, idx) => (
                  <div className="bundle-player-row" key={idx}>
                    <input
                      className="sa-input"
                      placeholder={`Player ${idx + 1} name${idx === 5 ? ' (optional)' : ''}`}
                      value={p.name}
                      onChange={(e) => {
                        const next = [...bundlePlayers];
                        next[idx].name = e.target.value;
                        setBundlePlayers(next);
                      }}
                    />
                    <input
                      id={`bundle-player-file-${idx}`}
                      type="file"
                      accept="image/png"
                      className="sa-input"
                      onChange={(e) => {
                        const next = [...bundlePlayers];
                        next[idx].file = e.target.files?.[0] || null;
                        setBundlePlayers(next);
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="sa-modal-actions">
                <button className="sa-btn ghost" type="button" onClick={() => setShowBundleModal(false)}>
                  Cancel
                </button>
                <button className="sa-btn" type="submit" disabled={mediaLoading}>
                  {mediaLoading ? 'Uploading...' : 'Create Team Bundle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editTarget && (
        <div className="sa-modal-backdrop" onClick={() => setEditTarget(null)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sa-modal-header">
              <h3>Edit Team Bundle</h3>
              <button className="sa-btn ghost" onClick={() => setEditTarget(null)}>
                Close
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await onUploadBundle({
                    teamId: editTeamId || editTarget.teamId,
                    teamName: editTeamName || editTarget.teamName,
                    logoFile: editLogo,
                    players: editPlayers.map((p) => ({ name: p.name, file: p.file, avatar: p.avatar })),
                    updateId: editTarget.id,
                  });
                } finally {
                  setEditTarget(null);
                  setEditLogo(null);
                }
              }}
              className="bundle-form"
            >
              <div className="bundle-row">
                <select
                  className="sa-select"
                  value={editTeamId}
                  onChange={(e) => setEditTeamId(e.target.value)}
                >
                  <option value="">Select team (optional)</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name || t.teamName || t.id}
                    </option>
                  ))}
                </select>
                <input
                  className="sa-input"
                  placeholder="Team name"
                  value={editTeamName}
                  onChange={(e) => setEditTeamName(e.target.value)}
                />
                <input
                  type="file"
                  accept="image/png"
                  className="sa-input"
                  onChange={(e) => setEditLogo(e.target.files?.[0] || null)}
                />
              </div>
              <div className="bundle-players">
                {editPlayers.map((p, idx) => (
                  <div className="bundle-player-row" key={idx}>
                    <input
                      className="sa-input"
                      placeholder={`Player ${idx + 1} name`}
                      value={p.name}
                      onChange={(e) => {
                        const next = [...editPlayers];
                        next[idx].name = e.target.value;
                        setEditPlayers(next);
                      }}
                    />
                    <input
                      type="file"
                      accept="image/png"
                      className="sa-input"
                      onChange={(e) => {
                        const next = [...editPlayers];
                        next[idx].file = e.target.files?.[0] || null;
                        setEditPlayers(next);
                      }}
                    />
                    {p.avatar && !p.file && (
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>Using existing avatar</div>
                    )}
                  </div>
                ))}
              </div>
              <div className="sa-modal-actions">
                <button className="sa-btn ghost" type="button" onClick={() => setEditTarget(null)}>
                  Cancel
                </button>
                <button className="sa-btn" type="submit" disabled={mediaLoading}>
                  {mediaLoading ? 'Updating...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="media-grid">
        {media.map((m) => (
          <div className="media-card" key={m.id}>
            <div
              className="media-header"
              onClick={() =>
                setExpandedMap(prev => ({ ...prev, [m.id]: !prev[m.id] }))
              }
              title="Show roster"
            >
              <div className="media-logo-block">
                {(m.logo || m.url) && (
                  <img src={m.logo || m.url} alt={m.type} className="media-thumb small" />
                )}
              </div>
              <div className="media-meta">
                <p className="media-type">{m.type === 'player' ? 'Player' : 'Team'}</p>
                <p className="media-team">{m.teamName || m.teamId || 'General'}</p>
                {m.playerName && <p className="media-player">{m.playerName}</p>}
              </div>
              <div className="media-toggle">
                {m.players && m.players.length > 0 ? (expandedMap[m.id] ? '▾' : '▸') : null}
              </div>
              <div className="media-actions-inline">
                <button
                  className="media-actions-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActionsId(actionsId === m.id ? null : m.id);
                  }}
                  title="Show actions"
                >
                  ⋯
                </button>
                {actionsId === m.id && (
                  <div className="media-actions-menu">
                    <button
                      className="sa-btn ghost"
                      onClick={() => {
                        setEditTarget(m);
                        setEditName(m.teamName || m.teamId || '');
                        setEditTeamId(m.teamId || '');
                        setEditTeamName(m.teamName || '');
                        setEditPlayers(
                          Array.isArray(m.players)
                            ? m.players.map((p) => ({ name: p.name || '', avatar: p.avatar || '', file: null }))
                            : []
                        );
                        setEditLogo(null);
                        setActionsId(null);
                      }}
                    >
                      Update
                    </button>
                    <button className="sa-btn ghost" onClick={() => onDelete(m)}>
                      Delete bundle
                    </button>
                  </div>
                )}
              </div>
            </div>
            {m.players && m.players.length > 0 && expandedMap[m.id] && (
              <div className="media-bundle">
                <p className="media-player">Roster:</p>
                <div className="media-bundle-grid">
                  {m.players.map((p, idx) => (
                    <div key={idx} className="media-bundle-item">
                      {p.avatar && <img src={p.avatar} alt={p.name || `Player ${idx + 1}`} className="media-bundle-thumb" />}
                      <span>{p.name || `Player ${idx + 1}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="media-actions">
            </div>
          </div>
        ))}
        {media.length === 0 && <div className="sa-empty">No media found.</div>}
      </div>
    </div>
  );
};

export default MediaLibrary;
